import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Baby, Calendar } from "lucide-react";
import { SleepPlanService } from "@/lib/services/sleep-plan-service";
import { SleepScheduleDialog } from "@/components/sleep-schedule/SleepScheduleDialog";
import { SleepPlan } from "@/types/sleep-plan";
import { differenceInMonths, format } from "date-fns";

interface Child {
  id: string;
  name: string;
  date_of_birth: string;
  latest_plan?: SleepPlan | null;
}

export default function MySleepSchedule() {
  const navigate = useNavigate();
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewingPlan, setViewingPlan] = useState<{ plan: SleepPlan; childName: string } | null>(null);

  useEffect(() => {
    loadChildren();
  }, []);

  const loadChildren = async () => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;

      // Get caregiver ID
      const { data: caregiverData } = await supabase
        .from('caregivers')
        .select('id')
        .eq('auth_user_id', userData.user.id)
        .single();

      if (!caregiverData) return;

      // Get children
      const { data: childrenData } = await supabase
        .from('children')
        .select('id, name, date_of_birth')
        .eq('caregiver_id', caregiverData.id)
        .eq('isdeleted', false)
        .or('archived.is.null,archived.eq.false');

      if (childrenData) {
        // Load sleep plans for each child
        const childrenWithPlans = await Promise.all(
          childrenData.map(async (child) => {
            const plan = await SleepPlanService.getLatestSleepPlan(child.id);
            return { ...child, latest_plan: plan };
          })
        );
        setChildren(childrenWithPlans);
      }
    } catch (error) {
      console.error('Error loading children:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSchedule = (child: Child) => {
    if (child.latest_plan) {
      setViewingPlan({ plan: child.latest_plan, childName: child.name });
    }
  };

  const calculateAge = (dateOfBirth: string) => {
    const months = differenceInMonths(new Date(), new Date(dateOfBirth));
    if (months < 12) {
      return `${months} month${months !== 1 ? 's' : ''}`;
    }
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (remainingMonths === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    }
    return `${years}y ${remainingMonths}m`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-accent/20">
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">My Baby's Sleep Schedule</h1>
          <p className="text-muted-foreground">
            View your personalized sleep schedules for each child
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : children.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Baby className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No children found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {children.map((child) => (
              <Card key={child.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Baby className="w-5 h-5 text-primary" />
                      <span>{child.name}</span>
                      <span className="text-sm text-muted-foreground font-normal">
                        ({calculateAge(child.date_of_birth)})
                      </span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {child.latest_plan ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <span>
                          Last updated: {format(new Date(child.latest_plan.created_at), 'MMMM d, yyyy')}
                        </span>
                      </div>
                      <Button
                        onClick={() => handleViewSchedule(child)}
                        className="w-full"
                      >
                        View Sleep Schedule
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-sm text-muted-foreground mb-3">
                        No sleep schedule available yet. Contact your specialist to create one.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <SleepScheduleDialog
        open={!!viewingPlan}
        onOpenChange={(open) => !open && setViewingPlan(null)}
        sleepPlan={viewingPlan?.plan || null}
        childName={viewingPlan?.childName || ''}
        isSpecialist={false}
      />
    </div>
  );
}
